import { useCallback } from 'react';

import type { CategoryNode } from '../../category-tree';
import { type usePublisherCategorySelector } from '../../publisher-category-selector/hooks/usePublisherCategorySelector';
import { type useSchoolCategorySelector } from '../../school-category-selector/hooks/useSchoolCategorySelector';
import { type useSubjectCategorySelector } from '../../subject-category-selector/hooks/useSubjectCategorySelector';

import type { useCategoryReset } from './useCategoryReset';

/**
 * Category actions hook - 노드 업데이트 라우팅
 *
 * CategoryNode의 type에 따라 적절한 state setter를 호출하고
 * 의존성 체인에 따른 cascading reset을 수행합니다.
 *
 * @example
 * const actions = useCategoryActions({
 *   schoolState,
 *   subjectState,
 *   publisherState,
 *   resetHandlers
 * });
 * actions.updateCategoryInfo(schoolTypeNode);
 */
export const useCategoryActions = ({
  schoolState,
  subjectState,
  publisherState,
  resetHandlers,
}: {
  schoolState: ReturnType<typeof useSchoolCategorySelector>;
  subjectState: ReturnType<typeof useSubjectCategorySelector>;
  publisherState: ReturnType<typeof usePublisherCategorySelector>;
  resetHandlers: ReturnType<typeof useCategoryReset>;
}) => {
  const updateCategoryInfo = useCallback(
    (node: CategoryNode) => {
      switch (node.type) {
        case 'SCHOOL_TYPE':
          schoolState.clearError('schoolTypeId');
          schoolState.schoolTypeState.setSelectedSchoolTypeNode(node);
          resetHandlers.resetFromSchoolType();
          break;

        case 'GRADE':
          schoolState.clearError('gradeId');
          schoolState.gradeState.setSelectedGradeNode(node);
          resetHandlers.resetFromGrade();
          break;

        case 'SEMESTER':
          schoolState.clearError('semesterId');
          schoolState.semesterState.setSelectedSemesterNode(node);
          resetHandlers.resetFromSemester();
          break;

        case 'SUBJECT':
          subjectState.clearError();
          subjectState.handleSelectSubject(node);
          resetHandlers.resetFromSubject();
          break;

        case 'SUB_SUBJECT':
          subjectState.clearError();
          subjectState.handleSelectSubSubject(node);
          resetHandlers.resetFromSubSubject();
          break;

        case 'PUBLISHER':
          publisherState.clearError('publisher');
          publisherState.setPublisher(node);
          break;

        case undefined:
          break;
      }
    },
    [schoolState, subjectState, publisherState, resetHandlers],
  );

  const updateCustomPublisher = useCallback(
    (customPublisher?: string) => {
      publisherState.setCustomPublisher(customPublisher);
    },
    [publisherState],
  );

  return {
    updateCategoryInfo,
    updateCustomPublisher,
  };
};
